// Core Components
import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { Redirect } from 'react-router-dom';

// Material UI Theme Imports
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { TextField, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { useSelector } from 'react-redux';
import { Button } from '../global/components/Button';

// Application Specific Imports
import { userLogin } from 'redux/actions';
import logo from 'assets/logo.svg';
import config from '../config';
import { get, size, noop } from 'global/lib/lodash';
import { Row } from 'global/components/row';
import { HelpModal } from './HelpModal';
import { Text } from 'global/components/text';
import { sendAuthCode } from '../redux/actions/userLogin';
import { stopProp } from '../global/utils';

const useStyles = makeStyles((theme) => ({
  appLogo: {
    width: '250px',
    height: '150px',
    margin: `${theme.spacing(1)} auto`,
    pointerEvents: 'none',
  },
  appLogin: {
    width: 'auto',
    display: 'block',
    marginLeft: theme.spacing.unit * 3,
    marginRight: theme.spacing.unit * 3,
    textAlign: 'center',
  },

  loginForm: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'space-around',
    marginTop: theme.spacing(1),
  },

  loginFormWrapper: {
    width: '100%',
    marginTop: theme.spacing(1),
  },

  button: {
    margin: theme.spacing(1),
  },

  policy: {
    display: 'block',
    marginTop: theme.spacing(2),
  },
}));

function LoginPage(props) {
  const [username, changeUsername] = useState('');
  const [password, changePassword] = useState('');
  const [code, setcode] = useState('');
  const [showPassword, changeShowPassword] = useState(false);
  const [state, setState] = useState({
    screen: 0,
    submits: 0,
  });
  const [message, setmessage] = useState(null);
  const loadedTheme = useSelector((state) => get(state, 'view.hasLoadedTheme'));
  const loadedAccount = useSelector((state) => get(state, 'auth.accountSettings'));
  const [modal, setModal] = useState({
    open: false,
    onClose: () => setModal((s) => ({ ...s, open: !s.open })),
  });

  const classes = useStyles();
  const { loggedIn } = props;

  const renderRedirect = (redirect) => {
    if (redirect && loadedTheme && !!size(loadedAccount)) {
      return <Redirect to="/home" />;
    }
  };

  const usernameInputChange = (event) => {
    changeUsername(event.target.value);
  };

  const passwordInputChange = (event) => {
    changePassword(event.target.value);
  };

  const textInputReset = (e) => {
    changeUsername('');
    changePassword('');
    changeShowPassword(false);
    e.preventDefault();
  };

  const handleDialogClose = () => {
    setmessage(null);
  };

  const handleLoginClick = async (e) => {
    try {
      stopProp(e);
      const usernameLogin = username !== undefined ? username : false;
      const passwordLogin = password !== undefined ? password : false;
      await props.dispatch(userLogin(usernameLogin, passwordLogin));
    } catch (err) {
      if (get(err, 'response.status') == 403) {
        setState((s) => ({ ...s, screen: 1 }));
      } else {
        setmessage(true);
      }
    }
  };

  const handleClickShowPassword = () => {
    changeShowPassword(!showPassword);
  };

  const back = () => {
    changePassword('');
    setState((s) => ({ ...s, screen: 0, submits: 0 }));
    setcode('');
  };

  const sendCode = async (e) => {
    try {
      stopProp(e);
      await props.dispatch(sendAuthCode(username, code));
      await handleLoginClick(e);
    } catch (err) {
      const text = get(err, 'response.data.message', 'Validation code does not match.');
      setmessage({ title: 'Validation Failed', text });
      setcode('');
    } finally {
      setState((s) => ({ ...s, submits: s.submits + 1 }));
    }
  };

  useEffect(() => {
    if (state.submits >= 10) {
      back();
      setmessage({ title: 'Too many tries', text: 'Please try again' });
    }
  }, [state.submits]);

  const renderAuthCode = () => {
    return (
      <div>
        <div style={{ margin: '10px auto', maxWidth: 500 }}>
          <Text>An account validation code has been sent to the email associated with your account. Please check your email and enter that code below.</Text>
        </div>

        <form noValidate autoComplete="off" onSubmit={!code ? noop : sendCode}>
          <TextField required id="code" label="Validation Code" className="login-username" value={code} onChange={(e) => setcode(e.target.value)} variant="outlined" style={{ width: '266px' }} />

          <Row center style={{ marginTop: 10 }}>
            <Button title="Back" className={classes.button} onClick={back} />
            <Button title="Resend Code" className={classes.button} onClick={handleLoginClick} />
            <Button type="submit" className={classes.button} color="primary" title="Confirm" disabled={!code} onClick={sendCode} />
          </Row>
        </form>
      </div>
    );
  };

  return (
    <div className={classes.appLogin}>
      <Dialog open={!!message} onClose={handleDialogClose} aria-labelledby="alert-dialog-title" aria-describedby="alert-dialog-description">
        <DialogTitle id="alert-dialog-title">{get(message, 'title', 'Login Attempt Failed')}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">{get(message, 'text', 'Your login credentials were not accepted.')}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose} color="primary" autoFocus>
            Oops
          </Button>
        </DialogActions>
      </Dialog>

      {renderRedirect(loggedIn)}

      <section className={classes.header}>
        <img src={logo} className={classes.appLogo} alt="logo" />
      </section>

      {state.screen === 0 && (
        <>
          <form className={classes.loginForm} noValidate autoComplete="off">
            <div className={classes.loginFormWrapper}>
              <TextField required id="username" label="Email" className="login-username" value={username} onChange={usernameInputChange} variant="outlined" style={{ width: '266px' }} />
            </div>
            <div className={classes.loginFormWrapper}>
              <TextField
                required
                id="password"
                label="Password"
                className="login-password"
                variant="outlined"
                type={showPassword ? 'text' : 'password'}
                value={password}
                style={{ marginTop: 'inherit' }}
                onChange={passwordInputChange}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton aria-label="Toggle password visibility" onClick={handleClickShowPassword} size="large">
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </div>
            <div className={classes.loginFormWrapper}>
              <Button
                type="submit"
                className={classes.button}
                style={{ width: '266px' }}
                variant="contained"
                color="primary"
                label="Login"
                data-testid="login-button-login"
                disabled={!username || !password}
                onClick={handleLoginClick}
                title="Login"
              />
            </div>
            <div className={classes.loginFormWrapper}>
              <Button label="Clear" data-testid="login-button-clear" className={classes.button} color="default" variant="contained" onClick={textInputReset} disabled={!username && !password}>
                Clear
              </Button>
              <Button
                label="Forgot Password"
                data-testid="login-button-forgot-password"
                className={classes.button}
                variant="contained"
                color="default"
                onClick={() => {
                  window.location.replace(`${config.api.baseUrl}password/reset`);
                }}
              >
                Forgot Password
              </Button>
            </div>
          </form>
          <Typography variant="caption" className={classes.policy}>
            By logging in you agree to the{' '}
            <a data-testid="login-a-privacy" href="/policy">
              Privacy Policy
            </a>{' '}
            and{' '}
            <a data-testid="login-a-terms" href="/policy/terms">
              User Agreement
            </a>
          </Typography>

          <Row center style={{ marginTop: 16 }}>
            <Button variant="contained" color="primary" data-testid="login-button-help" onClick={() => setModal((s) => ({ ...s, open: !s.open }))}>
              Need Help ?
            </Button>
          </Row>
          <HelpModal {...modal} />
        </>
      )}

      {state.screen === 1 && renderAuthCode()}
    </div>
  );
}

const mapStateToProps = (state) => {
  return {
    loggedIn: state.auth.accessToken,
    loginFailed: state.auth.loginFailed,
  };
};

export default connect(mapStateToProps)(LoginPage);
